import { Injectable } from '@angular/core';
import { EPlatformFeature } from '@enums/campaign.enum';
import {
  IFacebookPostApi,
  IInstagramPostApi,
  IInstagramReelApi,
  IInstagramStoryApi,
  ITiktokPostApi,
  ITwitchStreamApi,
  IXPostApi,
  IYoutubeShortApi,
  IYoutubeStreamApi,
  IYoutubeVodApi,
  TSocialContent,
} from '@interfaces/campaign-analytics-api.interface';
import { TNil } from '../app.interface';

@Injectable({
  providedIn: 'root',
})
export class SocialContentUtilitiesService {
  // Function overloads for type safety
  public getSocialContent(
    platformFeature: EPlatformFeature.twitchStream,
    socialContent: TSocialContent | TNil,
  ): ITwitchStreamApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.instagramPost,
    socialContent: TSocialContent | TNil,
  ): IInstagramPostApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.instagramReel,
    socialContent: TSocialContent | TNil,
  ): IInstagramReelApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.instagramStory,
    socialContent: TSocialContent | TNil,
  ): IInstagramStoryApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.tiktokPost,
    socialContent: TSocialContent | TNil,
  ): ITiktokPostApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.facebookPost,
    socialContent: TSocialContent | TNil,
  ): IFacebookPostApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.youtubeShort,
    socialContent: TSocialContent | TNil,
  ): IYoutubeShortApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.xPost,
    socialContent: TSocialContent | TNil,
  ): IXPostApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.youtubeVod,
    socialContent: TSocialContent | TNil,
  ): IYoutubeVodApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature.youtubeStream,
    socialContent: TSocialContent | TNil,
  ): IYoutubeStreamApi | TNil;
  public getSocialContent(
    platformFeature: EPlatformFeature,
    socialContent: TSocialContent | TNil,
  ): TSocialContent | TNil {
    switch (platformFeature) {
      case EPlatformFeature.twitchStream:
        return socialContent as ITwitchStreamApi;
      case EPlatformFeature.instagramPost:
        return socialContent as IInstagramPostApi;
      case EPlatformFeature.instagramReel:
        return socialContent as IInstagramReelApi;
      case EPlatformFeature.instagramStory:
        return socialContent as IInstagramStoryApi;
      case EPlatformFeature.tiktokPost:
        return socialContent as ITiktokPostApi;
      case EPlatformFeature.facebookPost:
        return socialContent as IFacebookPostApi;
      case EPlatformFeature.youtubeShort:
        return socialContent as IYoutubeShortApi;
      case EPlatformFeature.xPost:
        return socialContent as IXPostApi;
      case EPlatformFeature.youtubeVod:
        return socialContent as IYoutubeVodApi;
      case EPlatformFeature.youtubeStream:
        return socialContent as IYoutubeStreamApi;
      default:
        throw new Error(`No getSocialContent implementation for platform feature: ${platformFeature}`);
    }
  }

  public getStreamId(platformFeature: EPlatformFeature, socialContent: TSocialContent | TNil): string {
    switch (platformFeature) {
      case EPlatformFeature.twitchStream:
        return this.getSocialContent(platformFeature, socialContent)?.twitchMetaData?.twitchStreamId ?? '';
      case EPlatformFeature.youtubeStream:
        return this.getSocialContent(platformFeature, socialContent)?.youtubeLiveData?.youtubeUrl ?? '';
      default:
        throw new Error(`No getStreamId implementation for platform feature: ${platformFeature}`);
    }
  }

  public getContentUrl(platformFeature: EPlatformFeature, socialContent: TSocialContent | TNil): string {
    let url: string | undefined = '';

    switch (platformFeature) {
      case EPlatformFeature.twitchStream:
        url = this.getSocialContent(platformFeature, socialContent)?.twitchMetaData?.twitchUrl;
        break;
      case EPlatformFeature.instagramPost:
        url = this.getSocialContent(platformFeature, socialContent)?.instagramPostData?.originalUrl;
        break;
      case EPlatformFeature.instagramReel:
        url = this.getSocialContent(platformFeature, socialContent)?.instagramReelData?.originalUrl;
        break;
      case EPlatformFeature.instagramStory:
        url = this.getSocialContent(platformFeature, socialContent)?.instagramStoryData?.originalUrl;
        break;
      case EPlatformFeature.tiktokPost:
        url = this.getSocialContent(platformFeature, socialContent)?.tiktokData?.originalUrl;
        break;
      case EPlatformFeature.facebookPost:
        url = this.getSocialContent(platformFeature, socialContent)?.facebookData?.originalUrl;
        break;
      case EPlatformFeature.youtubeShort:
        url = this.getSocialContent(platformFeature, socialContent)?.youtubeData?.originalUrl;
        break;
      case EPlatformFeature.xPost:
        url = this.getSocialContent(platformFeature, socialContent)?.xData?.originalUrl;
        break;
      case EPlatformFeature.youtubeVod:
        url = this.getSocialContent(platformFeature, socialContent)?.youtubeData?.originalUrl;
        break;
      case EPlatformFeature.youtubeStream:
        url = this.getSocialContent(platformFeature, socialContent)?.youtubeLiveData?.youtubeUrl;
        break;
      default:
        throw new Error(`No getContentUrl implementation for platform feature: ${platformFeature}`);
    }

    return url ?? '';
  }
}
